import { useTranslation } from "react-i18next";
import { Filter, Plus } from "lucide-react";
import { Combobox, DateRangeField, FilterBlockEditor, MultiSelect } from "@tellescope/ui";
import { Select, Button } from "@tellescope/design-system";
import { EnduserSearchCombobox, type Enduser } from "@tellescope/contacts";
import {
  getInboxFreeformFieldOptions,
  getInboxOwnershipOptions,
  getInboxReadStatusOptions,
  getInboxStatusOptions,
  type InboxFilters as InboxFiltersModel,
  type InboxOwnershipFilter,
  type InboxReadStatusFilter,
} from "../../utils";
import { useInboxFilterConfigForm } from "./useInboxFilterConfigForm";

// TODO: remove once MultiSelect migrates to @tellescope/design-system — these classes only exist to
// override the ui Button base (font-medium, h-auto min-h-9, rounded-md, shadow-sm) so the trigger
// matches the design-system Select.Trigger next to it.
const multiSelectTriggerClassName = "h-8 min-h-8 w-full rounded-[10px] shadow-xs font-normal";

export interface InboxFilterProps {
  stagedFilters: InboxFiltersModel;
  onStagedFiltersChange: (filters: InboxFiltersModel) => void;
}

export const InboxFilter = ({ stagedFilters, onStagedFiltersChange }: InboxFilterProps) => {
  const { t } = useTranslation("inbox");
  const {
    channelOptions,
    userOptions,
    selectedAssigneeOptions,
    selectedCareTeamOptions,
    selectedPatients,
    tagOptions,
    customBlocks,
    setFilterField,
    setChannels,
    addCustomBlock,
    updateCustomBlock,
    removeCustomBlock,
  } = useInboxFilterConfigForm({ stagedFilters, onStagedFiltersChange });

  return (
    <div className="space-y-4 pt-4">
      {/* Two columns: these controls are all short (a select or a token field), so a single column
          left most of the dialog's width empty. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Ownership")}</span>
          <Select<InboxOwnershipFilter>
            value={stagedFilters.ownership ?? "all"}
            onValueChange={(value) => setFilterField("ownership", value === "all" ? undefined : value)}
          >
            <Select.Trigger data-testid="inbox-filter-ownership" className="w-full">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {getInboxOwnershipOptions().map(({ value, label }) => (
                <Select.Item key={value} value={value}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Read Status")}</span>
          <Select<InboxReadStatusFilter>
            value={stagedFilters.readStatus ?? "all"}
            onValueChange={(value) => setFilterField("readStatus", value === "all" ? undefined : value)}
          >
            <Select.Trigger data-testid="inbox-filter-read-status" className="w-full">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {getInboxReadStatusOptions().map(({ value, label }) => (
                <Select.Item key={value} value={value}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Inbox Status")}</span>
          <MultiSelect
            options={getInboxStatusOptions()}
            value={stagedFilters.inboxStatus ?? []}
            onValueChange={(value) => setFilterField("inboxStatus", value.length ? value : undefined)}
            placeholder={t("Any status")}
            id="inbox-filter-inbox-status"
            badgeListOutside
            className={multiSelectTriggerClassName}
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Channels")}</span>
          <MultiSelect
            options={channelOptions}
            value={stagedFilters.channels ?? []}
            onValueChange={setChannels}
            placeholder={t("Any channel")}
            id="inbox-filter-channels"
            badgeListOutside
            className={multiSelectTriggerClassName}
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Tags")}</span>
          <MultiSelect
            options={tagOptions}
            value={stagedFilters.tags ?? []}
            onValueChange={(tags) => setFilterField("tags", tags.length ? tags : undefined)}
            placeholder={t("Any tag")}
            badgeListOutside
            className={multiSelectTriggerClassName}
            creator={{
              render: (inputValue) => <span className="text-sm">{t('Add "{{tag}}"', { tag: inputValue })}</span>,
              isValidNewOption: (inputValue) => inputValue.trim().length > 0,
              transform: (inputValue) => inputValue.trim(),
              onCreate: (newOption) => setFilterField("tags", [...(stagedFilters.tags ?? []), newOption.value]),
            }}
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Assignees")}</span>
          <Combobox
            value={selectedAssigneeOptions}
            onChange={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((option) => option.value) : [];
              setFilterField("assignees", ids.length ? ids : undefined);
            }}
            options={userOptions}
            multiple
            clearable
            placeholder={t("Any assignee")}
            searchPlaceholder={t("Search users...")}
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Care Team")}</span>
          <Combobox
            value={selectedCareTeamOptions}
            onChange={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((option) => option.value) : [];
              setFilterField("careTeamIds", ids.length ? ids : undefined);
            }}
            options={userOptions}
            multiple
            clearable
            placeholder={t("Any care team member")}
            searchPlaceholder={t("Search users...")}
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Patients")}</span>
          <EnduserSearchCombobox
            value={selectedPatients}
            onChange={(endusers: Enduser[]) =>
              setFilterField("patients", endusers.length ? endusers.map(({ id }) => id) : undefined)
            }
            multiple
            placeholder={t("Any patient")}
            onlyDisplayCount
            showAsBadges
            clearable={false}
          />
        </div>

        <DateRangeField
          label={t("Received Date Range")}
          value={stagedFilters.dateRange ?? {}}
          onChange={(value) => setFilterField("dateRange", Object.keys(value).length ? value : undefined)}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{t("Custom Conditions")}</span>
          <Button variant="outline" size="xs" onClick={addCustomBlock} data-testid="inbox-filter-add-condition-block">
            <Plus className="h-2 w-2" />
            {t("Add Condition Block")}
          </Button>
        </div>
        <div className="flex flex-col items-stretch gap-2 w-full">
          {customBlocks.length ? (
            customBlocks.map((block, index) => (
              <div key={block.id} className="flex flex-col gap-4 items-stretch w-full">
                {index !== 0 && (
                  <div className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs w-min self-center">
                    {t("AND")}
                  </div>
                )}
                <FilterBlockEditor
                  className="w-full"
                  block={block}
                  fieldOptions={getInboxFreeformFieldOptions()}
                  onChange={(next) => updateCustomBlock(index, next)}
                  onRemove={() => removeCustomBlock(index)}
                />
              </div>
            ))
          ) : (
            <div className="rounded-sm border-dashed border-2 flex flex-col items-center py-10 text-muted-foreground text-sm font-light gap-2 w-full">
              <Filter size={36} className="text-gray-400" />
              <div className="flex flex-col items-center gap-0.5">
                <span>{t("No custom conditions added")}</span>
                <span className="text-xs">{t('Click "Add Condition Block" to create complex filters')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
